// Assuming the types for login, useNavigate, LoginResponse, and other types are defined in separate files

import React, { useEffect, useState } from 'react'
import { Formik, FormikHelpers } from 'formik'
import { useAppDispatch } from '../../../../redux/hook'
import InputField from '../../../../components/Form/InputField'
import { HIDE_LOADER, SHOW_LOADER } from '../../../../redux/slices/LoaderSlice'
import profile from '../../../../assets/images/default-placeholder.png'
import camera from '../../../../assets/svg/icon-camera.svg'
import './index.css'
import SelectField from '../../../../components/Form/Select'
import { useNavigate } from 'react-router-dom'
import { UserSchema } from '../schema'
import { Button } from 'react-bootstrap'
import { Location, Role, UserOnboarding } from '../type'
import {
    createUser,
    getLeavePolicy,
    getLocations,
    getLookupValues,
    getRoles,
    getUserLocationbased,
} from '../api'
import { SHOW_NOTIFICATION } from '../../../../redux/slices/showNotificationSlice'

const Signup: React.FC = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [locations, setLocations] = useState<Location[]>([])
    const [roles, setRoles] = useState<Role[]>([])
    const [image, setImage] = useState<{
        name: string
        extension: string
        data: string | ArrayBuffer | null
    }>({
        name: '',
        extension: '',
        data: null,
    })
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const [payfreqOptions, setPayFreqOptions] = useState<
        { label: string; value: string }[]
    >([])
    const [employmentTypeOptions, setEmploymentTypeOptions] = useState<
        { label: string; value: string }[]
    >([])
    const [leavepolicyOptions, setLeavepolicyOptions] = useState<
        { label: string; value: string }[]
    >([])
    const [locationid, setLocationID] = useState<string | null>(null)
    const [managerOptions, setManagerOptions] = useState<
        { label: string; value: string }[]
    >([])

    useEffect(() => {
        dispatch(SHOW_LOADER())
        const getLookUpData = async () => {
            try {
                const response = await getLocations()
                const role = await getRoles()
                const lookupValues = await getLookupValues()
                const leaveOptions = await getLeavePolicy()
                const resourceTypeData = lookupValues?.data?.filter(
                    (item: any) => item.lookUpType === 'PayFrequency'
                )
                // Transform filtered results into label and value format for ResourceType
                const transformedFreqTypeData = resourceTypeData.map(
                    (item: any) => ({
                        label: item.lookUpValue,
                        value: item.id,
                    })
                )
                const employmentTypeData = lookupValues?.data?.filter(
                    (item: any) => item.lookUpType === 'EmploymentType'
                )
                // Transform filtered results into label and value format for EmploymentType
                const transformedEmploymentTypeData = employmentTypeData.map(
                    (item: any) => ({
                        label: item.lookUpValue,
                        value: item.id,
                    })
                )
                // Set the state for EmploymentType options
                setEmploymentTypeOptions(transformedEmploymentTypeData)
                // Set the state for ResourceType options
                setPayFreqOptions(transformedFreqTypeData)
                const transformedRoles = role?.data?.map((role: any) => ({
                    value: role.name,
                    label: role.name,
                    id: role.id,
                }))
                const transformedLocations = response?.data?.data?.map(
                    (location: any) => ({
                        value: location.id,
                        label: location.name,
                    })
                )
                const transformedpolicy = leaveOptions?.data?.data?.map(
                    (location: any) => ({
                        value: location.id,
                        label: location.title,
                    })
                )
                setLeavepolicyOptions(transformedpolicy)
                setLocations(transformedLocations)
                setRoles(transformedRoles)
                dispatch(HIDE_LOADER())
            } catch (err) {
                // Handle any errors here
                console.error('Error fetching:', err)
                dispatch(HIDE_LOADER())
            }
        }

        getLookUpData()
    }, [])

    useEffect(() => {
        if (locationid) {
            const getManagerListing = async () => {
                try {
                    const response = await getUserLocationbased(locationid)
                    const transformedmanager = response?.data?.map(
                        (manager: any) => ({
                            value: manager.id,
                            label: manager.firstName + ' ' + manager.lastName,
                        })
                    )
                    setManagerOptions(transformedmanager)
                } catch (err) {
                    // Handle any errors here
                    console.error('Error fetching:', err)
                }
            }
            getManagerListing()
        }
    }, [locationid])

    const getUserTypeId = (userType: string) => {
        const role = roles.find((role) => role.value === userType)
        return role ? role.id : null
    }

    const handleSignup = async (
        values: UserOnboarding,
        actions: FormikHelpers<UserOnboarding>
    ) => {
        const payload = {
            image:
                image.name !== ''
                    ? {
                          name: image.name,
                          data: image.data,
                          extension: image.extension,
                      }
                    : null,
            locationId: values.location,
            userTypeId: getUserTypeId(values.usertype),
            joiningDate: new Date(values.joiningDate),
            firstName: values.firstName,
            lastName: values.lastName,
            middleName: values.middleName,
            email: values.email,
            phone: values.phone,
            assignedManagerId: values.manager === '' ? null : values.manager,
            employmentTypeId: values.employmentType,
            salaryOrHourlyRate:
                values.salaryRate === '' ? null : values.salaryRate,
            payFrequencyId:
                values.payfrequency === '' ? null : values.payfrequency,
            isEmployeeEarnsBonus: values.earningBonus === 'true' ? true : false,
            hoursWorkedPerWeek:
                values.workinghours === '' ? null : values.workinghours,
            yearlyInsurableEarnings:
                values.insurableEarning === '' ? null : values.insurableEarning,
            leavePolicyId: values.policy,
        }
        dispatch(SHOW_LOADER())

        try {
            const response = await createUser(payload)
            if (response) {
                dispatch(
                    SHOW_NOTIFICATION({
                        type: 'success',
                        message: 'User created successfully',
                    })
                )
                dispatch(HIDE_LOADER())
                navigate('/users/user-created', {
                    state: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        image: image ? image : null,
                    },
                })
            }
        } catch (err) {
            // Handle any errors here
            console.error('Error fetching:', err)
            dispatch(HIDE_LOADER())
        }
        actions.setSubmitting(false)
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] // Get the first selected file
        if (file) {
            const reader = new FileReader()
            reader.onloadend = () => {
                const imageDataUrl = reader.result as string // Convert result to base64 string
                setImagePreview(imageDataUrl)
                const name = file.name
                const extension = name.split('.').pop() || '' // Get file extension
                setImage({
                    name: name,
                    extension: `.${extension}`,
                    data: imageDataUrl,
                })
            }
            reader.readAsDataURL(file) // Read the file as a data URL
        }
    }

    return (
        <div className="bg-white h-[100vh] flex items-center card-box card-box-landing">
            <div className="card-box-center">
                <div className="upload-box">
                    <img
                        src={imagePreview ? imagePreview : profile}
                        alt="Logo"
                        className="icon-profile"
                    />
                    <label
                        className="file file--uploading"
                        htmlFor="input-file"
                    >
                        <input
                            id="input-file"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <img src={camera} alt="Logo" className="icon-camera" />
                    </label>
                </div>

                <div className="custom-form">
                    <Formik
                        initialValues={{
                            firstName: '',
                            lastName: '',
                            email: '',
                            phone: '',
                            employmentType: '',
                            joiningDate: '',
                            earningBonus: '',
                            policy: '',
                            location: '',
                            usertype: '',
                            middleName: '',
                            salaryRate: '',
                            workinghours: '',
                            insurableEarning: '',
                            payfrequency: '',
                            manager: '',
                        }}
                        validationSchema={UserSchema}
                        onSubmit={handleSignup}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <div className="row mrg-top">
                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <SelectField
                                                name="location"
                                                label="Location"
                                                options={[
                                                    {
                                                        value: '',
                                                        label: 'Select',
                                                    },
                                                    ...locations,
                                                ]}
                                                value={values.location}
                                                onChange={(e: any) => {
                                                    handleChange(e)
                                                    setLocationID(
                                                        e.target.value
                                                    )
                                                }}
                                                onBlur={handleBlur}
                                                error={errors.location}
                                                touched={touched.location}
                                                defaultValue="option1"
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <SelectField
                                                name="usertype"
                                                label="User Type"
                                                options={[
                                                    {
                                                        value: '',
                                                        label: 'Select',
                                                    },
                                                    ...roles,
                                                ]}
                                                value={values.usertype}
                                                onChange={(e) => {
                                                    handleChange(e)
                                                    if (
                                                        values.usertype ===
                                                        'Teacher'
                                                    ) {
                                                        setFieldValue(
                                                            'manager',
                                                            ''
                                                        )
                                                    }
                                                }}
                                                onBlur={handleBlur}
                                                error={
                                                    values.usertype ===
                                                    'Teacher'
                                                        ? errors.usertype
                                                        : ''
                                                }
                                                touched={
                                                    values.usertype ===
                                                        'Teacher' &&
                                                    touched.usertype
                                                }
                                                defaultValue="option1"
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="bg-green-color calendar">
                                            <InputField
                                                type="date"
                                                name="joiningDate"
                                                lable="Date of joining"
                                                value={values.joiningDate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.joiningDate}
                                                touched={touched.joiningDate}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <InputField
                                                type="text"
                                                lable={'First Name'}
                                                name="firstName"
                                                placeholder=""
                                                value={values.firstName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.firstName}
                                                touched={touched.firstName}
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <InputField
                                                type="text"
                                                lable={'Middle Name'}
                                                name="middleName"
                                                placeholder=""
                                                value={values.middleName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.middleName}
                                                touched={touched.middleName}
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <InputField
                                                type="text"
                                                lable={'Last Name'}
                                                name="lastName"
                                                placeholder=""
                                                value={values.lastName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.lastName}
                                                touched={touched.lastName}
                                            />
                                        </div>{' '}
                                    </div>
                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <InputField
                                                type="email"
                                                lable={'Email'}
                                                name="email"
                                                placeholder=""
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.email}
                                                touched={touched.email}
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <InputField
                                                type="text"
                                                lable={'Phone'}
                                                name="phone"
                                                placeholder=""
                                                value={values.phone}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.phone}
                                                touched={touched.phone}
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <SelectField
                                                name="manager"
                                                label="Assign Manager"
                                                options={[
                                                    {
                                                        value: '',
                                                        label: 'Select',
                                                    },
                                                    ...managerOptions,
                                                ]}
                                                value={values.manager}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.manager}
                                                touched={touched.manager}
                                                defaultValue="option1"
                                                disabled={
                                                    values.usertype !==
                                                    'Teacher'
                                                        ? true
                                                        : false
                                                }
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <SelectField
                                                name="employmentType"
                                                label="Employment Type"
                                                options={[
                                                    {
                                                        value: '',
                                                        label: 'Select',
                                                    },
                                                    ...employmentTypeOptions,
                                                ]}
                                                value={values.employmentType}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.employmentType}
                                                touched={touched.employmentType}
                                                defaultValue="option1"
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <InputField
                                                type="text"
                                                lable={'Salary/Hourly Rate'}
                                                name="salaryRate"
                                                placeholder="$"
                                                value={values.salaryRate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.salaryRate}
                                                touched={touched.salaryRate}
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <SelectField
                                                name="payfrequency"
                                                label="Pay Frequency"
                                                options={[
                                                    {
                                                        value: '',
                                                        label: 'Select',
                                                    },
                                                    ...payfreqOptions,
                                                ]}
                                                value={values.payfrequency}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.payfrequency}
                                                touched={touched.payfrequency}
                                                defaultValue="option1"
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <SelectField
                                                name="earningBonus"
                                                label="Employee Earns a Bonus"
                                                options={[
                                                    {
                                                        value: '',
                                                        label: 'Select',
                                                    },
                                                    {
                                                        value: 'false',
                                                        label: 'No',
                                                    },
                                                    {
                                                        value: 'true',
                                                        label: 'Yes',
                                                    },
                                                ]}
                                                value={values.earningBonus}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.earningBonus}
                                                touched={touched.earningBonus}
                                                defaultValue="option1"
                                            />
                                        </div>{' '}
                                    </div>
                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <InputField
                                                type="text"
                                                lable={'Hours worked per week'}
                                                name="workinghours"
                                                placeholder=""
                                                value={values.workinghours}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.workinghours}
                                                touched={touched.workinghours}
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <InputField
                                                type="number"
                                                lable={
                                                    'Yearly Insurable Earnings'
                                                }
                                                name="insurableEarning"
                                                placeholder=""
                                                value={values.insurableEarning}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.insurableEarning}
                                                touched={
                                                    touched.insurableEarning
                                                }
                                                onWheel={(e) =>
                                                    e.currentTarget.blur()
                                                }
                                            />
                                        </div>{' '}
                                    </div>

                                    <div className="col-sm-4">
                                        <div className="form-group bg-green-color">
                                            <SelectField
                                                name="policy"
                                                label="Leave Policy"
                                                options={[
                                                    {
                                                        value: '',
                                                        label: 'Select',
                                                    },
                                                    ...leavepolicyOptions,
                                                ]}
                                                value={values.policy}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.policy}
                                                touched={touched.policy}
                                                defaultValue="option1"
                                            />
                                        </div>{' '}
                                    </div>
                                </div>
                                <div className="mt-8">
                                    <div className="custom-btn-design custom-btn-design-block button-block profile">
                                        <Button
                                            type="submit"
                                            className="button button-primary me-3"
                                            style={{ width: 'auto' }}
                                        >
                                            SUBMIT INFO
                                        </Button>
                                        <Button
                                            className="upload-cancel button button-outline"
                                            style={{ width: 'auto' }}
                                            onClick={() => navigate('/users')}
                                        >
                                            CANCEL
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    )
}

export default Signup
